import {
  OnboardingStep,
  OnboardingStepStatus,
} from 'further-types/lp/onboarding';
import { VehicleApplication } from '../types';
import { useEffect, useState } from 'react';

const stepTitles = {
  [OnboardingStep.InvestorPortalView]: 'View investor portal',
  [OnboardingStep.InvestorDetails]: 'Request investor details completion',
  [OnboardingStep.InvestorClassification]:
    'Request investor classification completion',
  [OnboardingStep.KYCOrKYB]: 'Request KYC/KYB completion',
  [OnboardingStep.DataRoomView]: 'View data room',
  [OnboardingStep.TaxDeclarations]: 'Request FATCA/CRS/Tax confirmations',
  [OnboardingStep.DocumentSigning]: 'Request preset documents signing',
  [OnboardingStep.CommitmentConfirmation]: 'Request commitment information',
};

const stepDetails = {
  [OnboardingStep.InvestorPortalView]: null,
  [OnboardingStep.InvestorDetails]:
    'Require the investor to complete their personal information. Information you add on Investor details will be pre-completed for the investor in their registration flow.',
  [OnboardingStep.InvestorClassification]:
    'Require the investor to complete region-specific investor classification. This includes digital knowledge testing where appropriate.',
  [OnboardingStep.KYCOrKYB]:
    'Require the investor to complete digital eKYC or submit documents (as applicable). You can set your KYC/KYB document requests on your vehicle’s Fund setup page.',
  [OnboardingStep.DataRoomView]: null,
  [OnboardingStep.TaxDeclarations]:
    'Require the investor to complete a FATCA/CRS (and other custom tax) declaration. This is required ahead of accepting commitments in most jurisdictions.',
  [OnboardingStep.DocumentSigning]:
    'Require the investor to sign specific preset documents. If you want to add additional documents for this investor navigate to the Document signing page to process them for signing. You can also view sent documents there.',
  [OnboardingStep.CommitmentConfirmation]:
    'Require the investor to confirm their commitment and local currency. This step is automatically required if no commitment is input for this investor when document signing requests are made. If no target commitment or currency is added, the investor can add their own during this step. Fee templates set below are not shown to the investor.',
};

const disableNonCommitmentStepTooltip =
  'This step cannot be removed after the commitment has been completed.';
const updateCommitmentStepTooltip =
  'The target commitment cannot be adjusted or requested after the commitment has been made.';
const documentSigningDisabledTooltip =
  'No documents available. To add documents, please go to Document Signing.';

const stepSettings = {
  [OnboardingStep.InvestorPortalView]: {
    toggleable: false,
    showStatus: false,
    showReview: false,
  },
  [OnboardingStep.InvestorDetails]: {
    toggleable: true,
    showStatus: true,
    showReview: true,
  },
  [OnboardingStep.InvestorClassification]: {
    toggleable: true,
    showStatus: true,
    showReview: true,
  },
  [OnboardingStep.KYCOrKYB]: {
    toggleable: true,
    showStatus: true,
    showReview: true,
  },
  [OnboardingStep.DataRoomView]: {
    toggleable: false,
    showStatus: false,
    showReview: false,
  },
  [OnboardingStep.TaxDeclarations]: {
    toggleable: true,
    showStatus: true,
    showReview: true,
  },
  [OnboardingStep.DocumentSigning]: {
    toggleable: false,
    showStatus: false,
    showReview: false,
  },
  [OnboardingStep.CommitmentConfirmation]: {
    toggleable: true,
    showStatus: true,
    showReview: false,
  },
};

const defaultStepOrder = [
  OnboardingStep.InvestorPortalView,
  OnboardingStep.InvestorDetails,
  OnboardingStep.InvestorClassification,
  OnboardingStep.DataRoomView,
  OnboardingStep.KYCOrKYB,
  OnboardingStep.TaxDeclarations,
  OnboardingStep.DocumentSigning,
  OnboardingStep.CommitmentConfirmation,
];

function buildSteps(
  application: VehicleApplication,
  isDocumentsListEmpty: boolean,
  settingsOverride?: Array<{ step: OnboardingStep; isEnabled: boolean }>,
) {
  const allAvailableSteps = settingsOverride
    ? settingsOverride.map((s) => s.step)
    : [
        ...application.applicationSteps.map((s) => s.step),
        ...defaultStepOrder.filter(
          (step) => !application.applicationSteps.some((s) => s.step === step),
        ),
      ];

  return allAvailableSteps.map((step) => {
    const applicationStep = application.applicationSteps.find(
      (s) => s.step === step,
    );

    const isSigningDocumentsStepDisabled =
      step === OnboardingStep.DocumentSigning && isDocumentsListEmpty;

    const isStepEnabled =
      settingsOverride?.find((s) => s.step === step)?.isEnabled ??
      applicationStep?.status !== OnboardingStepStatus.Disabled;
    const isStepCompleted =
      applicationStep?.status === OnboardingStepStatus.Completed;

    const isCommitmentCompleted = !!application.commitment;
    const canNotDisableCommitmentStep =
      isCommitmentCompleted && step === OnboardingStep.CommitmentConfirmation;
    const canNotDisableNonCommitmentStep =
      isCommitmentCompleted && isStepEnabled && isStepCompleted;
    const shouldGreyOutDisabledStep =
      isCommitmentCompleted &&
      applicationStep?.status === OnboardingStepStatus.Disabled &&
      (step === OnboardingStep.InvestorPortalView ||
        step === OnboardingStep.DataRoomView);

    const disabled =
      canNotDisableNonCommitmentStep ||
      canNotDisableCommitmentStep ||
      isSigningDocumentsStepDisabled ||
      shouldGreyOutDisabledStep;

    const canUnapprove =
      !application.commitment && !!applicationStep?.canUnapprove;

    const forceReviewHidden =
      step === OnboardingStep.InvestorClassification &&
      (applicationStep?.status === OnboardingStepStatus.Requested ||
        applicationStep?.status === OnboardingStepStatus.Disabled);

    const showReview = forceReviewHidden
      ? false
      : stepSettings[step].showReview;

    return {
      key: step,
      title: stepTitles[step],
      details: stepDetails[step],
      isEnabled: isStepEnabled,

      status: applicationStep?.status,
      toggleable: stepSettings[step].toggleable,
      showStatus: stepSettings[step].showStatus,
      showReview,
      canUnapprove,
      disabled,
      disabledTooltip: isSigningDocumentsStepDisabled
        ? documentSigningDisabledTooltip
        : undefined,
      changeLog: applicationStep?.changeLog,
      toggleTooltip: canNotDisableCommitmentStep
        ? updateCommitmentStepTooltip
        : canNotDisableNonCommitmentStep
        ? disableNonCommitmentStepTooltip
        : undefined,
    };
  });
}

export default function useInvestorJourney({
  application,
  updateInvestorJourney,
  isDocumentsListEmpty,
}: {
  application: VehicleApplication;
  updateInvestorJourney: (data: {
    vehicleId: string;
    vehicleApplicationId: string;
    applicationSteps: Array<{ step: OnboardingStep; isEnabled: boolean }>;
  }) => void;
  isDocumentsListEmpty: boolean;
}) {
  const [steps, setSteps] = useState(() =>
    buildSteps(application, isDocumentsListEmpty),
  );

  useEffect(() => {
    setSteps(buildSteps(application, isDocumentsListEmpty));
  }, [application, isDocumentsListEmpty]);

  const handleToggleStep = (toggledStep: OnboardingStep) => {
    const applicationSteps = steps
      .map((s) => {
        if (s.key === toggledStep) {
          return {
            key: s.key,
            isEnabled: !s.isEnabled,
          };
        }
        return s;
      })
      .map((s) => ({ step: s.key, isEnabled: s.isEnabled }));

    setSteps(buildSteps(application, isDocumentsListEmpty, applicationSteps));
    updateInvestorJourney({
      vehicleId: application.vehicleId,
      vehicleApplicationId: application.id,
      applicationSteps,
    });
  };

  const handleStepsOrderChange = (orderedSteps: Array<OnboardingStep>) => {
    const applicationSteps = orderedSteps.map((s) => ({
      step: s,
      isEnabled: !!steps.find((s2) => s2.key === s)?.isEnabled,
    }));

    setSteps(buildSteps(application, isDocumentsListEmpty, applicationSteps));
    updateInvestorJourney({
      vehicleId: application.vehicleId,
      vehicleApplicationId: application.id,
      applicationSteps,
    });
  };

  return {
    steps,
    handleToggleStep,
    handleStepsOrderChange,
  };
}
